'use client';

import React, { useState, useEffect, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { apiClient } from '@/app/api/client';
import { articlesApi } from '@/app/api/articles';
import { apiConfig } from '@/config/api';
import { Button } from '@/components/common/Button';
import { SparklesIcon, CheckCircleIcon, PencilIcon, EyeIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { marked } from 'marked';

// Inline markdown helper for preview (bold/italic only)
const renderInlineMarkdown = (text: string): string => {
    if (!text) return '';
    return text
        // Bold+Italic (*** or ___)
        .replace(/\*\*\*([\s\S]+?)\*\*\*/g, '<strong><em>$1</em></strong>')
        .replace(/___([\s\S]+?)___/g, '<strong><em>$1</em></strong>')
        // Bold (** or __)
        .replace(/\*\*([\s\S]+?)\*\*/g, '<strong>$1</strong>')
        .replace(/__([\s\S]+?)__/g, '<strong>$1</strong>')
        // Italic (* or _)
        .replace(/\*([^\*]+?)\*/g, '<em>$1</em>')
        .replace(/_([^_]+?)_/g, '<em>$1</em>');
};

interface FeaturedSummaryWizardProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (summary: string, layout: 'vertical' | 'horizontal', ctaText: string) => void;
    articleId: string;
    articleTitle: string;
    existingSummary?: string;
    existingLayout?: 'vertical' | 'horizontal';
    existingCtaText?: string;
}

interface Template {
    id: string;
    name: string;
    description: string;
    icon: string;
}

interface Tone {
    id: string;
    name: string;
}

// Structured data for editing JSON summaries
interface EditableData {
    type: 'classic' | 'list' | 'steps' | 'quote';
    title?: string;
    intro?: string;
    text?: string;
    items?: Array<{ title: string; description: string }>;
    quote?: string;
    highlight?: string;
}

export default function FeaturedSummaryWizard({
    isOpen,
    onClose,
    onSave,
    articleId,
    articleTitle,
    existingSummary = '',
    existingLayout = 'vertical',
    existingCtaText = 'Read full story',
}: FeaturedSummaryWizardProps) {
    const [step, setStep] = useState<'template' | 'generate' | 'edit' | 'preview'>('template');
    const [templates, setTemplates] = useState<Template[]>([]);
    const [tones, setTones] = useState<Tone[]>([]);
    const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
    const [selectedToneId, setSelectedToneId] = useState<string>('');
    const [summary, setSummary] = useState(existingSummary);
    const [isLoading, setIsLoading] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
    const [selectedLayout, setSelectedLayout] = useState<'vertical' | 'horizontal'>('vertical');
    const [ctaText, setCtaText] = useState(existingCtaText);

    // Structured editing state
    const [editableData, setEditableData] = useState<EditableData | null>(null);
    const [isStructuredEdit, setIsStructuredEdit] = useState(false);

    // Load templates on open
    useEffect(() => {
        if (isOpen) {
            setStep(existingSummary ? 'preview' : 'template');
            setSummary(existingSummary);
            setSelectedLayout(existingLayout);
            setCtaText(existingCtaText);
            setError(null);
            setLoadingMessageIndex(0);
            loadTemplates();
        }
    }, [isOpen, existingSummary, existingLayout, existingCtaText]);

    // Cycle through loading messages when generating
    useEffect(() => {
        if (step !== 'generate') return;
        const interval = setInterval(() => {
            setLoadingMessageIndex(prev => (prev + 1) % 4);
        }, 2000);
        return () => clearInterval(interval);
    }, [step]);

    const loadTemplates = async () => {
        setIsLoading(true);
        try {
            const { templates: templateList, tones: toneList } = await articlesApi.getFeaturedSummaryTemplates();
            setTemplates(templateList);
            setTones(toneList);
            if (templateList.length > 0 && !selectedTemplateId) {
                setSelectedTemplateId(templateList[0].id);
            }
            if (toneList && toneList.length > 0 && !selectedToneId) {
                // Default to 'Professional' or first one
                const defaultTone = toneList.find(t => t.id === 'professional') || toneList[0];
                setSelectedToneId(defaultTone.id);
            }
        } catch (err) {
            console.error('Failed to load templates:', err);
            setError('Failed to load templates');
        } finally {
            setIsLoading(false);
        }
    };

    const handleGenerate = async () => {
        if (!selectedTemplateId) return;

        setIsGenerating(true);
        setError(null);
        setStep('generate');

        try {
            const result = await articlesApi.generateFeaturedSummary(articleId, selectedTemplateId, selectedToneId);
            setSummary(result.summary);
            if (result.ctaText) {
                setCtaText(result.ctaText);
            }
            setStep('edit');
        } catch (err: any) {
            console.error('Failed to generate summary:', err);
            // Extract error message from Axios response or fallback to generic message
            const errorMessage = err.response?.data?.message || err.message || 'Failed to generate summary';
            setError(errorMessage);
            setStep('template');
        } finally {
            setIsGenerating(false);
        }
    };

    const handleSave = async () => {
        if (!summary.trim()) {
            setError('Summary cannot be empty');
            return;
        }

        setIsSaving(true);
        setError(null);

        try {
            await articlesApi.saveFeaturedSummary(articleId, summary, selectedLayout, ctaText);
            onSave(summary, selectedLayout, ctaText);
            onClose();
        } catch (err: any) {
            console.error('Failed to save summary:', err);
            const errorMessage = err.response?.data?.message || err.message || 'Failed to save summary';
            setError(errorMessage);
        } finally {
            setIsSaving(false);
        }
    };

    const renderTemplateStep = () => {
        const selectedTone = tones.find(t => t.id === selectedToneId);

        return (
            <div className="space-y-6">
                {/* SECTION 1: Structure & Preview */}
                <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm space-y-4">
                    <div>
                        <h4 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                            <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-xs font-bold">1</span>
                            Choose Structure
                        </h4>
                    </div>

                    {isLoading ? (
                        <div className="flex justify-center py-8">
                            <div className="animate-spin h-8 w-8 border-2 border-blue-500 border-t-transparent rounded-full" />
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {templates.map((template) => (
                                <label
                                    key={template.id}
                                    className={`relative flex flex-col items-center text-center p-3 border-2 rounded-xl cursor-pointer transition-all h-full ${selectedTemplateId === template.id
                                        ? 'border-blue-500 bg-blue-50/50 hover:bg-blue-50'
                                        : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50'
                                        }`}
                                >
                                    <input
                                        type="radio"
                                        name="template"
                                        value={template.id}
                                        checked={selectedTemplateId === template.id}
                                        onChange={() => setSelectedTemplateId(template.id)}
                                        className="sr-only"
                                    />
                                    <div className="mb-2 p-2 rounded-lg bg-white border border-gray-100 shadow-sm text-gray-600">
                                        {template.id === 'list' && (
                                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                                            </svg>
                                        )}
                                        {template.id === 'classic' && (
                                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M12 5l7 7-7 7" />
                                            </svg>
                                        )}
                                        {template.id === 'quote' && (
                                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                                <path d="M14.017 21L14.017 18C14.017 16.8954 14.9124 16 16.017 16H19.017C19.5693 16 20.017 15.5523 20.017 15V9C20.017 8.44772 19.5693 8 19.017 8H15.017C14.4647 8 14.017 8.44772 14.017 9V11C14.017 11.5523 13.5693 12 13.017 12H12.017V5H22.017V15C22.017 18.3137 19.3307 21 16.017 21H14.017ZM5.0166 21L5.0166 18C5.0166 16.8954 5.91203 16 7.0166 16H10.0166C10.5689 16 11.0166 15.5523 11.0166 15V9C11.0166 8.44772 10.5689 8 10.0166 8H6.0166C5.46432 8 5.0166 8.44772 5.0166 9V11C5.0166 11.5523 4.56889 12 4.0166 12H3.0166V5H13.0166V15C13.0166 18.3137 10.3303 21 7.0166 21H5.0166Z" />
                                            </svg>
                                        )}
                                        {template.id === 'steps' && (
                                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                                            </svg>
                                        )}
                                        {!['list', 'classic', 'quote', 'steps'].includes(template.id) && template.icon}
                                    </div>
                                    <span className={`text-sm font-medium ${selectedTemplateId === template.id ? 'text-blue-700' : 'text-gray-900'}`}>{template.name}</span>
                                    {selectedTemplateId === template.id && (
                                        <div className="absolute top-2 right-2">
                                            <CheckCircleIcon className="w-4 h-4 text-blue-500" />
                                        </div>
                                    )}
                                </label>
                            ))}
                        </div>
                    )}

                    {/* Visual Preview embedded in Section 1 */}
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                        <div className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3 text-center">Preview</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-white rounded border border-gray-200 p-4 shadow-sm">
                            {/* Left: Visual Skeleton */}
                            <div className="h-32 flex flex-col justify-center border-r border-gray-100 pr-4">
                                {selectedTemplateId === 'classic' && (
                                    <div className="space-y-2">
                                        <div className="h-2 bg-gray-200 rounded w-full"></div>
                                        <div className="h-2 bg-gray-200 rounded w-full"></div>
                                        <div className="h-2 bg-gray-200 rounded w-3/4"></div>
                                    </div>
                                )}
                                {selectedTemplateId === 'list' && (
                                    <div className="space-y-2">
                                        <div className="h-2 bg-gray-200 rounded w-1/3 mb-1"></div>
                                        <div className="space-y-1.5 pl-2">
                                            <div className="flex items-center gap-2"><div className="w-1.5 h-1.5 bg-blue-400 rounded-full"></div><div className="h-1.5 bg-gray-200 rounded w-10/12"></div></div>
                                            <div className="flex items-center gap-2"><div className="w-1.5 h-1.5 bg-blue-400 rounded-full"></div><div className="h-1.5 bg-gray-200 rounded w-9/12"></div></div>
                                            <div className="flex items-center gap-2"><div className="w-1.5 h-1.5 bg-blue-400 rounded-full"></div><div className="h-1.5 bg-gray-200 rounded w-11/12"></div></div>
                                        </div>
                                    </div>
                                )}
                                {selectedTemplateId === 'steps' && (
                                    <div className="space-y-2">
                                        <div className="h-2 bg-gray-200 rounded w-1/3 mb-1"></div>
                                        <div className="space-y-1.5 pl-2">
                                            <div className="flex items-center gap-2"><div className="w-3 h-3 rounded bg-blue-100 text-[6px] flex items-center justify-center text-blue-600 font-bold">1</div><div className="h-1.5 bg-gray-200 rounded w-10/12"></div></div>
                                            <div className="flex items-center gap-2"><div className="w-3 h-3 rounded bg-blue-100 text-[6px] flex items-center justify-center text-blue-600 font-bold">2</div><div className="h-1.5 bg-gray-200 rounded w-9/12"></div></div>
                                            <div className="flex items-center gap-2"><div className="w-3 h-3 rounded bg-blue-100 text-[6px] flex items-center justify-center text-blue-600 font-bold">3</div><div className="h-1.5 bg-gray-200 rounded w-11/12"></div></div>
                                        </div>
                                    </div>
                                )}
                                {selectedTemplateId === 'quote' && (
                                    <div className="flex flex-col gap-2">
                                        <div className="h-2 bg-gray-200 rounded w-full mb-1"></div>
                                        <div className="flex gap-3 items-center">
                                            <div className="w-1 bg-blue-500 rounded-full h-12 opacity-30"></div>
                                            <div className="flex-1 space-y-2 py-1">
                                                <div className="h-2 bg-gray-800 rounded w-full opacity-10"></div>
                                                <div className="h-2 bg-gray-800 rounded w-11/12 opacity-10"></div>
                                                <div className="h-1.5 bg-blue-200 rounded w-1/3 mt-1"></div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {!selectedTemplateId && (
                                    <div className="text-center text-gray-400 text-sm">Select a structure</div>
                                )}
                            </div>

                            {/* Right: Explanations */}
                            <div className="flex flex-col justify-center text-sm">
                                {selectedTemplateId ? (
                                    <>
                                        <h5 className="font-semibold text-gray-900 mb-1">
                                            {selectedTemplateId === 'classic' && 'Standard Paragraph'}
                                            {selectedTemplateId === 'list' && 'Key Insights List'}
                                            {selectedTemplateId === 'steps' && 'Action Plan Order'}
                                            {selectedTemplateId === 'quote' && 'Quote Spotlight'}
                                        </h5>
                                        <p className="text-gray-600 text-xs leading-relaxed mb-2">
                                            {selectedTemplateId === 'classic' && 'A concise single-paragraph summary.'}
                                            {selectedTemplateId === 'list' && 'Quick-read bullet points highlighting top takeaways.'}
                                            {selectedTemplateId === 'steps' && 'Structure content into a numbered sequence.'}
                                            {selectedTemplateId === 'quote' && 'Focus on a powerful statement or testimonial.'}
                                        </p>
                                        <div className="text-xs bg-gray-50 p-2 rounded text-gray-500 border border-gray-100">
                                            <span className="font-medium text-gray-700">Best for: </span>
                                            {selectedTemplateId === 'classic' && 'News, brief updates, or general overviews.'}
                                            {selectedTemplateId === 'list' && 'Educational content, feature lists, or quick tips.'}
                                            {selectedTemplateId === 'steps' && 'Tutorials, how-to guides, or process explanations.'}
                                            {selectedTemplateId === 'quote' && 'Opinion pieces, interviews, or thought leadership.'}
                                        </div>
                                    </>
                                ) : (
                                    <p className="text-gray-400 italic">Select a template to see details.</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* SECTION 2: Tone Selection */}
                <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm space-y-4">
                    <h4 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                        <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-xs font-bold">2</span>
                        Choose Tone
                    </h4>

                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {tones.map((tone) => (
                            <button
                                key={tone.id}
                                onClick={() => setSelectedToneId(tone.id)}
                                className={`px-3 py-2 text-sm rounded-lg border text-left transition-all ${selectedToneId === tone.id
                                    ? 'border-blue-500 bg-blue-50 text-blue-700 font-medium shadow-sm'
                                    : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                                    }`}
                            >
                                {tone.name}
                            </button>
                        ))}
                    </div>

                    {selectedTone && (
                        <div className="bg-blue-50/50 rounded-lg p-3 border border-blue-100 flex gap-3 text-sm text-blue-900">
                            <div className="flex-shrink-0 mt-0.5">
                                <SparklesIcon className="w-4 h-4 text-blue-500" />
                            </div>
                            <div>
                                <span className="font-semibold mr-1">{selectedTone.name}:</span>
                                {selectedTone.id === 'hook' && 'Create curiosity with a bold question or statement.'}
                                {selectedTone.id === 'teaser' && 'Reveal just enough to be interesting, holding back the main value.'}
                                {selectedTone.id === 'professional' && 'Use clear, authoritative, and direct business language.'}
                                {selectedTone.id === 'educational' && 'Focus on learning outcomes and practical value.'}
                                {selectedTone.id === 'urgent' && 'Create a sense of urgency or missing out.'}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const renderGenerateStep = () => {
        const loadingMessages = [
            'Analyzing your article...',
            'Crafting the perfect hook...',
            'Generating your featured summary...',
            'Almost there...'
        ];

        return (
            <div className="flex flex-col items-center justify-center py-12">
                <div className="relative mb-6">
                    <div className="animate-spin h-12 w-12 border-4 border-blue-200 border-t-blue-500 rounded-full" />
                    <SparklesIcon className="absolute inset-0 m-auto w-5 h-5 text-blue-500" />
                </div>
                <p className="text-gray-700 font-medium">{loadingMessages[loadingMessageIndex]}</p>
                <p className="text-sm text-gray-400 mt-2">This may take 5-10 seconds</p>
            </div>
        );
    };

    const renderEditStep = () => {
        // Try to parse as JSON for structured editing
        let parsedData: EditableData | null = null;
        try {
            const parsed = JSON.parse(summary);
            if (parsed && parsed.type) {
                parsedData = parsed as EditableData;
            }
        } catch (e) {
            // Not JSON, show legacy textarea mode
        }

        // Helper to update a field and sync back to summary
        const updateField = (field: keyof EditableData, value: any) => {
            if (!parsedData) return;
            const updated = { ...parsedData, [field]: value };
            setSummary(JSON.stringify(updated, null, 2));
        };

        // Helper to update an item in the items array
        const updateItem = (index: number, field: 'title' | 'description', value: string) => {
            if (!parsedData?.items) return;
            const updatedItems = [...parsedData.items];
            updatedItems[index] = { ...updatedItems[index], [field]: value };
            const updated = { ...parsedData, items: updatedItems };
            setSummary(JSON.stringify(updated, null, 2));
        };

        // Add a new item
        const addItem = () => {
            if (!parsedData) return;
            const newItems = [...(parsedData.items || []), { title: '', description: '' }];
            const updated = { ...parsedData, items: newItems };
            setSummary(JSON.stringify(updated, null, 2));
        };

        // Remove an item
        const removeItem = (index: number) => {
            if (!parsedData?.items) return;
            const updatedItems = parsedData.items.filter((_, i) => i !== index);
            const updated = { ...parsedData, items: updatedItems };
            setSummary(JSON.stringify(updated, null, 2));
        };

        // If not JSON, show legacy textarea
        if (!parsedData) {
            return (
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <p className="text-sm text-gray-600">
                            Edit your summary. Aim for 150-250 characters.
                        </p>
                        <span className={`text-xs px-2 py-1 rounded ${summary.length < 100 ? 'bg-yellow-100 text-yellow-700' :
                            summary.length > 300 ? 'bg-red-100 text-red-700' :
                                'bg-green-100 text-green-700'
                            }`}>
                            {summary.length} characters
                        </span>
                    </div>
                    <textarea
                        value={summary}
                        onChange={(e) => setSummary(e.target.value)}
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 resize-none"
                        placeholder="Enter your featured summary..."
                    />
                    <p className="text-xs text-gray-500">
                        💡 Tip: Use **bold** and *italic* for emphasis (Markdown supported)
                    </p>
                </div>
            );
        }

        // Structured JSON editor
        return (
            <div className="space-y-5">
                {/* Type indicator */}
                <div className="flex items-center gap-2 text-sm">
                    <span className="text-gray-500">Type:</span>
                    <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full font-medium capitalize">
                        {parsedData.type}
                    </span>
                </div>

                {/* Display Title - For all types */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Display Title <span className="text-gray-400 font-normal">(optional, overrides article title)</span>
                    </label>
                    <input
                        type="text"
                        value={parsedData.title || ''}
                        onChange={(e) => updateField('title', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Short, punchy title (30-60 chars)"
                    />
                </div>

                {/* Classic: Single text field */}
                {parsedData.type === 'classic' && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Summary Text
                        </label>
                        <textarea
                            value={parsedData.text || ''}
                            onChange={(e) => updateField('text', e.target.value)}
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 resize-none"
                            placeholder="Enter your summary..."
                        />
                    </div>
                )}

                {/* List/Steps/Quote: Intro field */}
                {(parsedData.type === 'list' || parsedData.type === 'steps' || parsedData.type === 'quote') && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Intro Text
                        </label>
                        <textarea
                            value={parsedData.intro || ''}
                            onChange={(e) => updateField('intro', e.target.value)}
                            rows={2}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 resize-none"
                            placeholder="Short introductory sentence..."
                        />
                        <p className="text-xs text-gray-400 mt-1">Aim for 80-120 characters</p>
                    </div>
                )}

                {/* List/Steps: Items editor */}
                {(parsedData.type === 'list' || parsedData.type === 'steps') && (
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <label className="block text-sm font-medium text-gray-700">
                                {parsedData.type === 'steps' ? 'Steps' : 'Key Points'}
                            </label>
                            <button
                                type="button"
                                onClick={addItem}
                                className="text-xs px-2 py-1 bg-blue-50 text-blue-600 rounded hover:bg-blue-100 transition-colors"
                            >
                                + Add Item
                            </button>
                        </div>
                        <div className="space-y-3">
                            {parsedData.items?.map((item, index) => (
                                <div key={index} className="flex gap-2 items-start p-3 bg-gray-50 rounded-lg border border-gray-200">
                                    <span className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${parsedData.type === 'steps' ? 'bg-blue-100 text-blue-600' : 'bg-green-100 text-green-600'}`}>
                                        {index + 1}
                                    </span>
                                    <div className="flex-1 space-y-2">
                                        <input
                                            type="text"
                                            value={item.title}
                                            onChange={(e) => updateItem(index, 'title', e.target.value)}
                                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="Title (2-4 words)"
                                        />
                                        <input
                                            type="text"
                                            value={item.description}
                                            onChange={(e) => updateItem(index, 'description', e.target.value)}
                                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="Description (6-10 words)"
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => removeItem(index)}
                                        className="text-gray-400 hover:text-red-500 transition-colors p-1"
                                        title="Remove item"
                                    >
                                        <XMarkIcon className="w-4 h-4" />
                                    </button>
                                </div>
                            ))}
                            {(!parsedData.items || parsedData.items.length === 0) && (
                                <p className="text-sm text-gray-400 text-center py-4">
                                    No items yet. Click "Add Item" to start.
                                </p>
                            )}
                        </div>
                    </div>
                )}

                {/* Quote: Quote text and highlight */}
                {parsedData.type === 'quote' && (
                    <>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Quote
                            </label>
                            <textarea
                                value={parsedData.quote || ''}
                                onChange={(e) => updateField('quote', e.target.value)}
                                rows={2}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 resize-none italic"
                                placeholder="Powerful statement or quote..."
                            />
                            <p className="text-xs text-gray-400 mt-1">Aim for 60-120 characters</p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Highlight (optional)
                            </label>
                            <input
                                type="text"
                                value={parsedData.highlight || ''}
                                onChange={(e) => updateField('highlight', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500"
                                placeholder="Key word or phrase to emphasize"
                            />
                        </div>
                    </>
                )}
            </div>
        );
    };



    const renderPreviewStep = () => {
        // Try to parse summary as JSON
        let summaryData: any = null;
        let isJson = false;
        try {
            summaryData = JSON.parse(summary);
            if (summaryData && summaryData.type) {
                isJson = true;
            }
        } catch (e) {
            isJson = false;
        }

        // Fallback for markdown/legacy
        const renderedHtml = !isJson ? marked.parse(summary, { async: false }) as string : '';

        return (
            <div className="space-y-6">
                {/* Layout Selector */}
                <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-gray-700">Display Layout:</p>
                    <div className="flex bg-gray-100 p-1 rounded-lg">
                        <button
                            onClick={() => setSelectedLayout('vertical')}
                            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${selectedLayout === 'vertical' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            Vertical
                        </button>
                        <button
                            onClick={() => setSelectedLayout('horizontal')}
                            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${selectedLayout === 'horizontal' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            Horizontal
                        </button>
                    </div>
                </div>

                {/* Card Preview */}
                <div className="border border-gray-200 rounded-xl p-6 bg-gray-50">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3 text-center">Live Card Preview</p>
                    <div className="w-full bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                        <div className={`p-8 ${selectedLayout === 'horizontal' ? 'grid md:grid-cols-2 md:grid-rows-[auto,auto] gap-x-12 gap-y-6 items-start' : 'flex flex-col gap-6'}`}>

                            {/* Primary Column (Text) */}
                            <div className="flex flex-col h-full">
                                <div className="flex gap-2 mb-4">
                                    <span className="bg-blue-100 text-blue-700 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide">Category</span>
                                </div>
                                <h3 className="text-4xl font-extrabold text-gray-900 mb-4 leading-tight">
                                    {(isJson && summaryData.title) ? summaryData.title : (articleTitle || 'Article Title Placeholder')}
                                </h3>

                                <div className="text-gray-600 mb-6 flex-grow">
                                    {isJson ? (
                                        // Structured Intro with inline markdown
                                        <p
                                            className="leading-relaxed"
                                            dangerouslySetInnerHTML={{
                                                __html: renderInlineMarkdown(
                                                    (summaryData.type === 'list' || summaryData.type === 'steps' || summaryData.type === 'quote')
                                                        ? (summaryData.intro || '')
                                                        : (summaryData.text || '')
                                                )
                                            }}
                                        />
                                    ) : (
                                        // Legacy Markdown
                                        <div
                                            className="prose prose-sm max-w-none prose-p:my-1 prose-ul:my-2 prose-li:my-0.5"
                                            dangerouslySetInnerHTML={{ __html: renderedHtml }}
                                        />
                                    )}
                                </div>

                                {/* Footer - Author Info Only */}
                                <div className="flex items-center justify-between pt-2 mt-auto">
                                    <div className="flex items-center gap-2 text-xs text-gray-400">
                                        <span>Author Name</span>
                                        <span>•</span>
                                        <span>5 min read</span>
                                    </div>
                                </div>
                            </div>

                            {/* Secondary Column (Visual Items - List/Quote) */}
                            {isJson && (summaryData.type === 'list' || summaryData.type === 'steps' || summaryData.type === 'quote') && (
                                <div className={`cg-card-secondary ${selectedLayout === 'horizontal' ? 'md:col-start-2 md:row-start-1 md:row-span-2 flex flex-col justify-center min-h-[160px]' : 'mt-2 border-t border-gray-100 pt-6'}`}>
                                    {/* LIST / STEPS */}
                                    {(summaryData.type === 'list' || summaryData.type === 'steps') && summaryData.items && (
                                        <ul className="space-y-4">
                                            {summaryData.items.map((item: any, index: number) => (
                                                <li key={index} className="flex gap-3">
                                                    <span className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold mt-0.5 ${summaryData.type === 'steps' ? 'bg-blue-100 text-blue-600' : 'bg-green-100 text-green-600'
                                                        }`}>
                                                        {index + 1}
                                                    </span>
                                                    <div>
                                                        <strong
                                                            className="block text-gray-900 text-sm font-semibold mb-0.5"
                                                            dangerouslySetInnerHTML={{ __html: renderInlineMarkdown(item.title) }}
                                                        />
                                                        <span
                                                            className="text-sm text-gray-600 leading-snug block"
                                                            dangerouslySetInnerHTML={{ __html: renderInlineMarkdown(item.description) }}
                                                        />
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    )}

                                    {/* QUOTE */}
                                    {summaryData.type === 'quote' && summaryData.quote && (
                                        <blockquote className="relative pl-14 py-2 italic text-gray-900 font-medium text-2xl leading-relaxed font-serif">
                                            <span className="absolute -left-2 -top-6 text-8xl text-blue-500 opacity-40 font-serif leading-none select-none">"</span>
                                            <span dangerouslySetInnerHTML={{ __html: `"${renderInlineMarkdown(summaryData.quote)}"` }} />
                                        </blockquote>
                                    )}
                                </div>
                            )}



                            {/* CTA - Now outside */}
                            <div className={`flex items-center gap-1 group text-sm font-medium text-blue-600 cursor-pointer hover:text-blue-800 transition-colors ${selectedLayout === 'horizontal' ? 'md:col-start-1 md:row-start-2 md:self-end' : 'mt-0'}`}>
                                {ctaText || 'Read full story'}
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 transform transition-transform group-hover:translate-x-1">
                                    <path fillRule="evenodd" d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z" clipRule="evenodd" />
                                </svg>
                            </div>

                        </div>
                    </div>
                </div>

                {/* CTA Text Input - Below Preview */}
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Call-to-Action Text
                    </label>
                    <input
                        type="text"
                        value={ctaText}
                        onChange={(e) => setCtaText(e.target.value)}
                        placeholder="Read full story"
                        className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    />
                    <p className="mt-1.5 text-xs text-gray-500">
                        This text appears as the link at the bottom of the card
                    </p>
                </div>
            </div>
        );
    };


    const getStepContent = () => {
        switch (step) {
            case 'template': return renderTemplateStep();
            case 'generate': return renderGenerateStep();
            case 'edit': return renderEditStep();
            case 'preview': return renderPreviewStep();
        }
    };

    const getStepTitle = () => {
        switch (step) {
            case 'template': return 'Choose Template';
            case 'generate': return 'Generating...';
            case 'edit': return 'Edit Summary';
            case 'preview': return 'Preview';
        }
    };

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={() => { }}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/30" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-2xl bg-white p-8 text-left align-middle shadow-xl transition-all">
                            <Dialog.Title as="h3" className="text-xl font-medium leading-6 text-gray-900 mb-4 flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <SparklesIcon className="w-5 h-5 text-purple-500" />
                                    Featured Summary
                                </div>
                                <button
                                    onClick={onClose}
                                    className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                                >
                                    <span className="sr-only">Close</span>
                                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                                </button>
                            </Dialog.Title>

                            {step === 'template' ? (
                                <p className="text-sm text-gray-500 mb-6">
                                    Select a template to generate an engaging summary for your landing page or featured content section.
                                    These summaries are optimized for high-conversion styling.
                                    <a
                                        href={`${apiConfig.wwwUrl}/demo/featured/`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-600 hover:text-blue-700 ml-1 inline-flex items-center gap-0.5"
                                    >
                                        View live demo
                                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                        </svg>
                                    </a>
                                </p>
                            ) : (
                                <p className="text-xs text-gray-500 mb-4">{getStepTitle()}</p>
                            )}

                            {error && (
                                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                                    <p className="text-sm text-red-600">{error}</p>
                                </div>
                            )}

                            {getStepContent()}

                            <div className="mt-6 flex justify-between items-center">
                                {/* Left side - always Cancel/Close except when generating */}
                                <div>
                                    {step !== 'generate' && (
                                        <Button variant="ghost" onClick={onClose}>Cancel</Button>
                                    )}
                                </div>

                                {/* Right side - Action buttons */}
                                <div className="flex space-x-3">
                                    {step === 'template' && (
                                        <Button
                                            onClick={handleGenerate}
                                            disabled={!selectedTemplateId || isLoading}
                                        >
                                            <SparklesIcon className="w-4 h-4 mr-1" />
                                            Generate
                                        </Button>
                                    )}

                                    {step === 'edit' && (
                                        <>
                                            <Button variant="ghost" onClick={() => setStep('template')}>
                                                ← Back
                                            </Button>
                                            <Button onClick={() => setStep('preview')}>
                                                <EyeIcon className="w-4 h-4 mr-1" />
                                                Preview
                                            </Button>
                                        </>
                                    )}

                                    {step === 'preview' && (
                                        <>
                                            <Button variant="ghost" onClick={() => setStep('edit')}>
                                                <PencilIcon className="w-4 h-4 mr-1" />
                                                Edit
                                            </Button>
                                            <Button onClick={handleSave} disabled={isSaving}>
                                                {isSaving ? 'Saving...' : 'Save Summary'}
                                            </Button>
                                        </>
                                    )}
                                </div>
                            </div>
                        </Dialog.Panel>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
}
